"use client";
import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
    checkCustomerIsExist,
    getTicketPriority,
    getTicketType,
    storeSupportTicketEnquiry,
} from "@/lib/support_api/service";
import HCaptcha from "@hcaptcha/react-hcaptcha";

function EnquirySupportTicket({ syslanguageuid }: any) {
    const isDE = syslanguageuid == "1"; // GERMAN SWITCH

    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState(null);
    const [ticketTypes, setTicketTypes] = useState<any[]>([]);
    const [ticketPriorities, setTicketPriorities] = useState<any[]>([]);
    const [showToastMessages, setShowToastMessages]: any = useState(null);

    const recaptchaRef = useRef<any>(null);
    const siteKey: any = process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY;
    const company = process.env.NEXT_PUBLIC_BASE_COMPANY;

    const initialValues = {
        subject: "",
        raised_by: "",
        summary: "",
        ticket_type: "",
        priority: "",
        description: "",
        via_customer_portal: "1",
        custom_company: company,
        captcha: null,
    };

    const validationSchema = Yup.object({
        raised_by: Yup.string()
            .email(isDE ? "Bitte geben Sie eine gültige E-Mail ein." : "Please enter a valid email.")
            .required(isDE ? "E-Mail ist erforderlich." : "Email is required.")
            .test(
                "is-member",
                isDE ? "Sie haben keine aktive Mitgliedschaft." : "You have no active membership.",
                async function (value) {
                    if (!value) return true;
                    setLoading(true);
                    try {
                        const res = await checkCustomerIsExist(value);
                        setLoading(false);
                        return res?.data?.length > 0;
                    } catch (err) {
                        setLoading(false);
                        return this.createError({
                            message: isDE
                                ? "Mitgliedschaft konnte nicht überprüft werden."
                                : "Unable to verify membership.",
                        });
                    }
                }
            ),

        subject: Yup.string().required(isDE ? "Betreff ist erforderlich." : "Subject is required."),
        summary: Yup.string().required(isDE ? "Zusammenfassung ist erforderlich." : "Summary is required."),
        priority: Yup.string().required(isDE ? "Priorität ist erforderlich." : "Priority is required."),
        ticket_type: Yup.string().required(isDE ? "Tickettyp ist erforderlich." : "Ticket type is required."),
        captcha: Yup.string().required(
            isDE ? "Bitte bestätigen Sie, dass Sie kein Roboter sind." : "Please verify that you are human."
        ),
    });

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const ticketTypeRes = await getTicketType('["name"]', "999999999");
                const ticketPriorityRes = await getTicketPriority('["name"]', "999999999");

                if (ticketTypeRes?.data) setTicketTypes(ticketTypeRes.data);
                if (ticketPriorityRes?.data) setTicketPriorities(ticketPriorityRes.data);
            } catch (error) {
                console.error("Error fetching dropdown options", error);
            }
        };

        fetchOptions();
    }, []);

    const submitForm = async (values: any, { resetForm }: any) => {
        if (!token) {
            setShowToastMessages({
                type: "error",
                message: isDE
                    ? "Bitte schließen Sie die hCaptcha-Verifizierung ab."
                    : "Please complete the hCaptcha verification.",
            });
            return;
        }

        setLoading(true);

        const payload = {
            subject: values?.subject,
            raised_by: values?.raised_by,
            summary: values?.summary,
            ticket_type: values?.ticket_type,
            priority: values?.priority,
            description: values?.description,
            via_customer_portal: values?.via_customer_portal,
            custom_company: company,
        };

        try {
            const customerCheck = await checkCustomerIsExist(values?.raised_by);

            if (customerCheck?.data?.length > 0) {
                const response = await storeSupportTicketEnquiry(payload);

                if (response?.type === "success") {
                    resetForm();
                    setToken(null);

                    setShowToastMessages({
                        type: "success",
                        message: isDE
                            ? "Danke! Wir haben Ihr Support-Ticket erhalten."
                            : "Thank you! We've received your support ticket.",
                    });
                } else {
                    setShowToastMessages({
                        type: "error",
                        message:
                            response?.message ||
                            (isDE ? "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut." : "Something went wrong."),
                    });
                }
            } else {
                setShowToastMessages({
                    type: "error",
                    message: isDE ? "Sie haben keine aktive Mitgliedschaft." : "You have no active membership.",
                });
            }
        } catch (error) {
            setShowToastMessages({
                type: "error",
                message: isDE
                    ? "Ein unerwarteter Fehler ist aufgetreten."
                    : "An unexpected error occurred.",
            });
        } finally {
            resetForm();
            setToken(null);
            recaptchaRef.current.resetCaptcha();
            setLoading(false);
        }
    };

    return (
        <>
            <div className={`fw-bold error ${showToastMessages?.type === "error" ? "text-danger" : "text-success"}`}>
                <p>{showToastMessages?.message}</p>
            </div>

            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submitForm}>
                {({ handleSubmit, setFieldValue }) => (
                    <Form onSubmit={handleSubmit} noValidate>
                        {/* Email */}
                        <div className="row form-group mb-3">
                            <div className="col-lg-12">
                                <label>{isDE ? "E-Mail" : "Email"} *</label>
                                <Field
                                    className="form-control"
                                    name="raised_by"
                                    placeholder={isDE ? "E-Mail eingeben" : "Enter Email"}
                                />
                                <ErrorMessage name="raised_by" component="p" className="text-danger mb-0" />
                            </div>
                        </div>

                        {/* Subject */}
                        <div className="row form-group mb-3">
                            <div className="col-lg-12">
                                <label>{isDE ? "Betreff" : "Subject"} *</label>
                                <Field
                                    className="form-control"
                                    name="subject"
                                    placeholder={isDE ? "Betreff eingeben" : "Enter Subject"}
                                />
                                <ErrorMessage name="subject" component="p" className="text-danger mb-0" />
                            </div>
                        </div>

                        {/* Ticket Type & Priority */}
                        <div className="row form-group mb-3">
                            <div className="col-lg-6">
                                <label>{isDE ? "Tickettyp" : "Ticket Type"} *</label>
                                <Field
                                    as="select"
                                    name="ticket_type"
                                    className="form-control"
                                    onChange={(e: any) => setFieldValue("ticket_type", e.target.value)}
                                >
                                    <option value="">{isDE ? "Tickettyp auswählen" : "Select Ticket Type"}</option>
                                    {ticketTypes.map((t) => (
                                        <option key={t.name} value={t.name}>
                                            {t.name}
                                        </option>
                                    ))}
                                </Field>
                                <ErrorMessage name="ticket_type" component="p" className="text-danger mb-0" />
                            </div>

                            <div className="col-lg-6">
                                <label>{isDE ? "Priorität" : "Priority"} *</label>
                                <Field
                                    as="select"
                                    name="priority"
                                    className="form-control"
                                    onChange={(e: any) => setFieldValue("priority", e.target.value)}
                                >
                                    <option value="">{isDE ? "Priorität auswählen" : "Select Priority"}</option>
                                    {ticketPriorities.map((p) => (
                                        <option key={p.name} value={p.name}>
                                            {p.name}
                                        </option>
                                    ))}
                                </Field>
                                <ErrorMessage name="priority" component="p" className="text-danger mb-0" />
                            </div>
                        </div>

                        {/* Summary */}
                        <div className="row form-group mb-3">
                            <div className="col-lg-12">
                                <label>{isDE ? "Zusammenfassung" : "Summary"}</label>
                                <Field
                                    as="textarea"
                                    name="summary"
                                    className="form-control"
                                    placeholder={isDE ? "Zusammenfassung eingeben" : "Enter Summary"}
                                    rows="3"
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="row form-group mb-3">
                            <div className="col-lg-12">
                                <label>{isDE ? "Beschreibung" : "Description"}</label>
                                <Field
                                    as="textarea"
                                    name="description"
                                    className="form-control"
                                    placeholder={isDE ? "Beschreibung eingeben" : "Enter Description"}
                                    rows="3"
                                />
                            </div>
                        </div>

                        {/* Captcha */}
                        <div className="row form-group mb-3">
                            <HCaptcha
                                sitekey={siteKey}
                                ref={recaptchaRef}
                                onVerify={(token: any) => {
                                    setToken(token);
                                    setFieldValue("captcha", token);
                                }}
                                onExpire={() => {
                                    setToken(null);
                                    setFieldValue("captcha", "");
                                }}
                                onError={() => {
                                    setToken(null);
                                    setFieldValue("captcha", "");
                                }}
                            />
                            <ErrorMessage name="captcha" component="p" className="text-danger mb-0" />
                        </div>

                        <button
                            type="submit"
                            className={`theme-btn ${loading ? "loading cursor-not-allowed" : ""}`}
                        >
                            {isDE ? "Absenden" : "Submit"} <i className="far fa-arrow-right"></i>
                        </button>
                    </Form>
                )}
            </Formik>
        </>
    );
}

export default EnquirySupportTicket;
